// src/hooks/useResponsive.js
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

/**
 * Comprehensive responsive hook for perfect device detection and breakpoint management
 * Provides device info, breakpoints, orientation, and responsive utilities
 */
export const useResponsive = () => {
  const theme = useTheme();
  
  // MUI Breakpoint queries
  const isXs = useMediaQuery(theme.breakpoints.only('xs')); // 0-599px
  const isSm = useMediaQuery(theme.breakpoints.only('sm')); // 600-959px  
  const isMd = useMediaQuery(theme.breakpoints.only('md')); // 960-1279px
  const isLg = useMediaQuery(theme.breakpoints.only('lg')); // 1280-1919px
  const isXl = useMediaQuery(theme.breakpoints.only('xl')); // 1920px+
  
  // Range queries
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 960px
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-959px
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // >= 960px
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1280px
  const isUltraWide = useMediaQuery(theme.breakpoints.up('xl')); // >= 1920px
  
  // Custom breakpoints for dashboard
  const isTinyMobile = useMediaQuery('(max-width: 480px)'); // Very small phones
  const isSmallMobile = useMediaQuery('(max-width: 640px)'); // Small phones
  const isSidebarBreakpoint = useMediaQuery(theme.breakpoints.up('md')); // >= 960px (sidebar permanent)
  const isStatsCardBreakpoint = useMediaQuery(theme.breakpoints.up('md')); // 4x1 layout
  
  // Device state
  const [deviceInfo, setDeviceInfo] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    orientation: 'landscape',
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    isTouch: false,
    isRetina: false,
  });

  // Update device info on resize and orientation change
  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const orientation = width > height ? 'landscape' : 'portrait';
      const devicePixelRatio = window.devicePixelRatio || 1;
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isRetina = devicePixelRatio > 1;

      setDeviceInfo({
        width,
        height,
        orientation,
        devicePixelRatio,
        isTouch,
        isRetina,
      });
    };

    // Initial call
    updateDeviceInfo();

    // Event listeners
    window.addEventListener('resize', updateDeviceInfo);
    window.addEventListener('orientationchange', updateDeviceInfo);
    
    return () => {
      window.removeEventListener('resize', updateDeviceInfo);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  // Device type detection
  const getDeviceType = () => {
    if (isTinyMobile) return 'tiny-mobile';
    if (isXs) return 'mobile';
    if (isSm) return 'tablet';
    if (isMd) return 'desktop';
    if (isLg) return 'large-desktop';
    if (isXl) return 'ultra-wide';
    return 'unknown';
  };

  // Responsive values helper
  const getResponsiveValue = (values) => {
    if (typeof values === 'object' && values !== null) {
      if (isXl && values.xl !== undefined) return values.xl;
      if (isLg && values.lg !== undefined) return values.lg;
      if (isMd && values.md !== undefined) return values.md;
      if (isSm && values.sm !== undefined) return values.sm;
      if (isXs && values.xs !== undefined) return values.xs;
      return values.default || values.xs || Object.values(values)[0];
    }
    return values;
  };

  // Grid system helpers
  const getGridColumns = () => {
    if (isTinyMobile) return 1;
    if (isMobile) return 2; // 2x2 for stats cards
    if (isTablet) return 3;
    if (isDesktop) return 4; // 4x1 for stats cards
    if (isLargeDesktop) return 5;
    if (isUltraWide) return 6;
    return 4;
  };

  // Spacing helpers
  const getSpacing = (base = 3) => {
    return getResponsiveValue({
      xs: base,
      sm: base + 1,
      md: base + 2,
      lg: base + 3,
      xl: base + 4,
    });
  };

  // Typography helpers
  const getFontSize = (variant = 'body1') => {
    const sizes = {
      h1: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem', xl: '4rem' },
      h2: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem', xl: '3.5rem' },
      h3: { xs: '1.5rem', sm: '1.75rem', md: '2rem', lg: '2.25rem', xl: '2.5rem' },
      h4: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem', xl: '2.25rem' },
      h5: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem', lg: '1.75rem', xl: '2rem' },
      h6: { xs: '1rem', sm: '1.1rem', md: '1.25rem', lg: '1.5rem', xl: '1.75rem' },
      body1: { xs: '0.875rem', sm: '1rem', md: '1rem', lg: '1.125rem', xl: '1.25rem' },
      body2: { xs: '0.75rem', sm: '0.875rem', md: '0.875rem', lg: '1rem', xl: '1.125rem' },
      caption: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem', lg: '0.875rem', xl: '1rem' },
    };
    
    return getResponsiveValue(sizes[variant] || sizes.body1);
  };

  // Chart dimensions
  const getChartHeight = () => {
    return getResponsiveValue({
      xs: 240,
      sm: 280,
      md: 320,
      lg: 400,
      xl: 480,
    });
  };

  // Sidebar helpers
  const getSidebarWidth = () => {
    return getResponsiveValue({
      xs: 0, // Hidden on mobile
      sm: 0, // Hidden on small screens
      md: 280, // Permanent sidebar width
      lg: 300,
      xl: 320,
    });
  };

  const shouldShowSidebar = () => isSidebarBreakpoint;
  const shouldUsePermanentSidebar = () => isDesktop;
  const shouldUseTemporarySidebar = () => isMobile;

  // Data grid helpers
  const getDataGridDensity = () => {
    return isMobile ? 'compact' : 'standard';
  };

  const getDataGridPageSize = () => {
    return getResponsiveValue({
      xs: 10,
      sm: 15,
      md: 25,
      lg: 50,
      xl: 100,
    });
  };

  // Touch helpers
  const getMinTouchTarget = () => 44; // Always 44px for accessibility
  
  const shouldUseTouch = () => deviceInfo.isTouch || isMobile;

  // Animation helpers
  const shouldReduceMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const getAnimationDuration = (base = 300) => {
    if (shouldReduceMotion()) return 0;
    return isMobile ? base * 0.8 : base; // Slightly faster on mobile
  };

  // Layout helpers
  const getContainerMaxWidth = () => {
    return getResponsiveValue({
      xs: 'sm',
      sm: 'md', 
      md: 'lg',
      lg: 'xl',
      xl: false, // No max width on ultra-wide
    });
  };

  const getCardElevation = () => {
    return isMobile ? 1 : 2; // Less elevation on mobile for performance
  };

  // Performance helpers
  const shouldUseVirtualization = () => isDesktop;
  const shouldLazyLoad = () => isMobile;
  const shouldPreloadImages = () => isDesktop && !deviceInfo.isTouch;

  // Utility functions
  const isPortrait = () => deviceInfo.orientation === 'portrait';
  const isLandscape = () => deviceInfo.orientation === 'landscape';
  const isRetina = () => deviceInfo.isRetina;
  const isHighDensity = () => deviceInfo.devicePixelRatio > 2;

  // Debug helpers (for development)
  const getDebugInfo = () => ({
    breakpoint: getDeviceType(),
    width: deviceInfo.width,
    height: deviceInfo.height,
    orientation: deviceInfo.orientation,
    isTouch: deviceInfo.isTouch,
    isRetina: deviceInfo.isRetina,
    devicePixelRatio: deviceInfo.devicePixelRatio,
    flags: {
      isXs, isSm, isMd, isLg, isXl,
      isMobile, isTablet, isDesktop, isLargeDesktop, isUltraWide,
      isTinyMobile, isSmallMobile, isSidebarBreakpoint, isStatsCardBreakpoint,
    },
  });

  return {
    // Breakpoint flags
    isXs, isSm, isMd, isLg, isXl,
    isMobile, isTablet, isDesktop, isLargeDesktop, isUltraWide,
    isTinyMobile, isSmallMobile, isSidebarBreakpoint, isStatsCardBreakpoint,
    
    // Device info
    deviceInfo,
    deviceType: getDeviceType(),
    
    // Responsive helpers
    getResponsiveValue,
    getGridColumns,
    getSpacing,
    getFontSize,
    getChartHeight,
    
    // Sidebar helpers
    getSidebarWidth,
    shouldShowSidebar,
    shouldUsePermanentSidebar,
    shouldUseTemporarySidebar,
    
    // Data grid helpers
    getDataGridDensity,
    getDataGridPageSize,
    
    // Touch & accessibility
    getMinTouchTarget,
    shouldUseTouch,
    
    // Animation helpers
    shouldReduceMotion,
    getAnimationDuration,
    
    // Layout helpers
    getContainerMaxWidth,
    getCardElevation,
    
    // Performance helpers
    shouldUseVirtualization,
    shouldLazyLoad,
    shouldPreloadImages,
    
    // Utility functions
    isPortrait,
    isLandscape,
    isRetina,
    isHighDensity,
    
    // Debug
    getDebugInfo,
  };
};

export default useResponsive;